import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import { createResourceLocatorString } from '../../util/routes';
import { getRentMoreUrl } from '../../util/sessionStorageManager';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney, convertPrice } from '../../util/currency';
import { DATE_RANGE_PARAMS } from '../../util/dateRangeHelpers';
import { isSafeInternalPath } from '../../util/urlHelpers';
import { groupCartItemsByAuthorAndDateRange } from '../../ducks/cart.duck';

const { Money } = sdkTypes;

// checkExpiredRentalDates now lives in util/cartHelpers.js so the side carts (components)
// can share it without importing from a container; re-exported here so this page's
// existing imports keep working.
export { checkExpiredRentalDates } from '../../util/cartHelpers';

/**
 * Calculate subtotal from line items with currency conversion
 * @param {Array} lineItems - Array of line items
 * @param {String} currency - Target currency code
 * @param {Object} intl - React Intl object
 * @param {Array} exchangeRates - Exchange rates for currency conversion
 * @returns {String} Formatted subtotal amount
 */
export const calculateSubTotal = (lineItems, currency, intl, exchangeRates = []) => {
    if (!lineItems?.length) return null;

    const subTotalAmount = lineItems
        .filter(({ includeFor }) => includeFor[0] === 'customer' && includeFor[1] === 'provider')
        .reduce((total, item) => {
            // Convert each line item to user's currency before summing
            const fromCurrency = item.lineTotal.currency;
            const convertedPrice = convertPrice(
                exchangeRates,
                item.lineTotal.amount,
                fromCurrency,
                currency
            );
            return total + (convertedPrice?.amount || 0);
        }, 0);

    return formatMoney(intl, new Money(subTotalAmount, currency));
};

/**
 * Calculate full customer total from line items (including platform fees).
 * Sums all line items where includeFor contains 'customer'.
 */
export const calculateCustomerTotal = (lineItems, currency, intl, exchangeRates = []) => {
    if (!lineItems?.length) return null;

    const totalAmount = lineItems
        .filter(({ includeFor }) => Array.isArray(includeFor) && includeFor.includes('customer'))
        .reduce((total, item) => {
            const fromCurrency = item.lineTotal.currency;
            const convertedPrice = convertPrice(
                exchangeRates,
                item.lineTotal.amount,
                fromCurrency,
                currency
            );
            return total + (convertedPrice?.amount || 0);
        }, 0);

    return formatMoney(intl, new Money(totalAmount, currency));
};

/**
 * Get the redirect URL for "Rent More Items" button
 * Uses sessionStorage only (session-based, not persisted in cart items)
 * 
 * Priority:
 * 1. Saved URL from sessionStorage (search page or store page)
 * 2. Author's store page (fallback)
 * 3. Search page (ultimate fallback)
 * 
 * @param {Object} routeConfiguration - Route configuration
 * @param {String} authorId - Author/store owner ID
 * @returns {String} Redirect URL
 */
export const getRentMoreItemsUrl = (routeConfiguration, authorId, firstItemDates, timeZone) => {
    // 1. Check sessionStorage for saved URL from this session
    const { startDate, endDate } = firstItemDates || {};
    // Calendar days in the booking's own zone - the store page reads these back in
    // the store's zone, so a browser-local day here would land a cross-zone shopper
    // on the wrong day.
    const toDay = d => (timeZone ? moment(d).tz(timeZone) : moment(d)).format('YYYY-MM-DD');
    let startDateParam = '';
    let endDateParam = '';
    if (startDate) {
        startDateParam = toDay(startDate);
    }
    if (endDate) {
        endDateParam = toDay(endDate);
    }

    // savedUrl traces back to the `searchUrl` query param (attacker-controllable via a
    // crafted link), persisted via saveRentMoreUrl and later handed to history.push - so
    // it's revalidated here rather than trusted as stored, in case an unsafe value made
    // it into sessionStorage before this check existed.
    const savedUrl = getRentMoreUrl(authorId);
    if (savedUrl && isSafeInternalPath(savedUrl)) {
        return savedUrl;
    }

    // 2. Fallback: redirect to author's store page
    if (authorId) {
        return createResourceLocatorString('ProfilePageLegacy', routeConfiguration, { id: authorId }, { startDate: startDateParam, endDate: endDateParam });
    }

    // 3. Ultimate fallback: search page
    return createResourceLocatorString('SearchPage', routeConfiguration, {}, {});
};

/**
 * Get the item with the largest booking period from the cart items
 * Used to determine the base item for transaction initiation
 * @param {Array} items - Array of cart items
 * @returns {Object} Item with the largest booking period
 */
export const getItemWithLargestBookingPeriod = (items) => {
    if (!items?.length) return null;

    let largestPeriodItem = null;
    let largestPeriod = 0;

    items.forEach(item => {
        const { startDate, endDate } = item.bookingDates || {};
        if (!startDate || !endDate) return;

        const start = moment.tz(startDate, item.timeZone || 'Etc/UTC').unix();
        const end = moment.tz(endDate, item.timeZone || 'Etc/UTC').unix();
        const duration = end - start;

        if (duration > largestPeriod) {
            largestPeriod = duration;
            largestPeriodItem = item;
        }
    });

    return largestPeriodItem;
};

/**
 * Hourly price package identifiers
 */
export const HOURLY_PRICE_PACKAGES = ['perMinutePrice', 'perHourPrice'];

/**
 * Check if running on mobile device
 * @returns {Boolean}
 */
export const isMobileDevice = () => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
};

/**
 * Build fetch line items payload from cart items
 * @param {Array} items - Cart items for an author
 * @returns {Array} fetchLineItemsData payload
 */
export const buildFetchLineItemsPayload = (items = []) => {
    return items
        .filter(item => {
            // Skip items whose listing has been deleted — listing object must be present.
            // listingId alone is not sufficient because the API returns 404 for deleted listings.
            const resolvedId = item.listing?.id?.uuid || item.listingId;
            return !!resolvedId && !!item.listing?.id;
        })
        .map(item => {
            const {
                bookingDates,
                timeZone,
                quantity,
                package: selectedPackage,
                listingId,
                listing,
                usageMultipleValues,
                otherOptionalFees,
                selectedFeeTypes,
                pickupAtOwnerLocation,
                returnAtOwnerLocation,
            } = item;

            const startDate = moment(bookingDates.startDate).tz(timeZone).toDate();
            const endDate = moment(bookingDates.endDate).tz(timeZone).toDate();

            return {
                orderData: {
                    bookingStart: startDate,
                    bookingEnd: endDate,
                    seats: parseInt(quantity, 10),
                    package: selectedPackage,
                    usageMultipleValues,
                    selectedFeeTypes: selectedFeeTypes || otherOptionalFees || [],
                    pickupAtOwnerLocation,
                    returnAtOwnerLocation,
                },
                listingId: listingId || listing?.id?.uuid,
                isOwnListing: false,
            };
        });
};

/**
 * Get next available author ID when current becomes empty
 * Items are pre-sorted by recency in cart.duck.js, so authorIds[0] is always the most recent
 * @param {Object} items - Cart items grouped by authorId (pre-sorted by recency)
 * @param {String} currentAuthorId - Current active author
 * @returns {String|null} Next author ID or null
 */
export const getNextAuthorId = (items, currentAuthorId) => {
    const authorIds = Object.keys(items || {});
    if (!authorIds.length) return null;

    // Prefer keeping current if still valid
    if (currentAuthorId && items[currentAuthorId]?.length > 0) {
        return currentAuthorId;
    }

    // Return first author (most recent, since items are pre-sorted)
    return authorIds[0] || null;
};

/**
 * Convert line items to user's currency
 * @param {Object} lineItems - Line items grouped by authorId
 * @param {Array} exchangeRates - Exchange rates for conversion
 * @param {String} targetCurrency - User's preferred currency
 * @returns {Object} Converted line items
 */
export const convertLineItemsCurrency = (lineItems, exchangeRates = [], targetCurrency) => {
    if (!lineItems || !exchangeRates?.length || !targetCurrency) {
        return lineItems;
    }

    const convertedLineItems = {};

    Object.keys(lineItems).forEach(authorId => {
        const items = lineItems[authorId];
        if (!items || !Array.isArray(items)) {
            convertedLineItems[authorId] = items;
            return;
        }

        convertedLineItems[authorId] = items.map(item => {
            // Convert lineTotal
            const convertedLineTotal = item.lineTotal
                ? convertPrice(
                    exchangeRates,
                    item.lineTotal.amount,
                    item.lineTotal.currency,
                    targetCurrency
                )
                : item.lineTotal;

            // Convert unitPrice
            const convertedUnitPrice = item.unitPrice
                ? convertPrice(
                    exchangeRates,
                    item.unitPrice.amount,
                    item.unitPrice.currency,
                    targetCurrency
                )
                : item.unitPrice;

            return {
                ...item,
                lineTotal: convertedLineTotal || item.lineTotal,
                unitPrice: convertedUnitPrice || item.unitPrice,
            };
        });
    });

    return convertedLineItems;
};

/**
 * The cart page's model: ONE cart per (store, date range), keyed by the group's own
 * stable key, most recently added first (Object insertion order is what the
 * active-cart hook and getNextAuthorId rely on). Built from the same
 * groupCartItemsByAuthorAndDateRange the side panels and the All Open Carts panel
 * use, so every cart surface agrees on what an "order" is.
 *
 * The cart page used to render one card per STORE, straight off the per-author
 * `state.cart.items` map, so a store booked on two different weekends showed - and
 * checked out - as one combined order. Items belong together only when both the
 * store and the rental dates match.
 *
 * @param {Object} itemsByAuthor - state.cart.items ({ [authorId]: items[] })
 * @returns {Object} { [groupKey]: items[] }
 */
export const buildCartItemsByGroupKey = itemsByAuthor =>
    groupCartItemsByAuthorAndDateRange(itemsByAuthor || {}).reduce((acc, group) => {
        acc[group.key] = group.items;
        return acc;
    }, {});

/**
 * Line items are stored per cart-group key, but checkout (startCheckoutFromItems /
 * CheckoutPage) also looks them up per item under the real author id - alias the
 * active group's line items under that id too so both readers find them.
 */
export const aliasLineItemsForAuthor = (lineItems = {}, groupKey, authorId) =>
    groupKey && authorId && lineItems[groupKey]
        ? { ...lineItems, [authorId]: lineItems[groupKey] }
        : lineItems;
